import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Minus, Plus, ShoppingBag, ChevronRight, Sparkles, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useApp } from '@/state/AppContext';
import { inr } from '@/lib/format';
import { createShopifyCheckout } from '@/lib/api';
import { FreeShippingBar } from '@/components/FreeShippingBar';
import { Footer } from '@/components/Footer';
import { imgSrc } from '@/lib/img';

const CartPage = () => {
  const { cart, updateQty, removeItem, subtotal, shipping, total, clearCart, config } = useApp();
  const navigate = useNavigate();
  const [redirecting, setRedirecting] = useState(false);

  const shopifyEnabled = !!config?.shopify_enabled;

  const handleCheckout = async () => {
    // If Shopify is live, skip our internal address form and hand off directly
    // to Shopify's hosted checkout (which collects address + payment in one flow).
    if (!shopifyEnabled) {
      navigate('/checkout');
      return;
    }
    setRedirecting(true);
    try {
      const items = cart.map((c) => ({
        product_id: c.product_id,
        product_slug: c.product_slug,
        name: c.name,
        qty: c.qty,
        shopify_variant_id: c.shopify_variant_id || null,
        customization: c.customization || null,
        pack: c.pack || null,
        customised: !!(c.customised || c.customization),
      }));
      const res = await createShopifyCheckout(items);
      if (res?.url) {
        window.location.href = res.url;
        return;
      }
      toast.error('Checkout unavailable — try again in a moment.');
      navigate('/checkout'); // fallback
    } catch (e) {
      toast.error('Could not start checkout', { description: e?.response?.data?.detail || 'Please try again.' });
      navigate('/checkout');
    } finally {
      setRedirecting(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-10" data-testid="cart-page">
        <section className="rounded-3xl bb-block-yellow p-8 text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-white flex items-center justify-center">
            <ShoppingBag className="w-7 h-7 text-[var(--bb-cyan)]" />
          </div>
          <h1 className="font-display text-3xl text-[var(--bb-darkblue)] mt-3">Your cart is empty</h1>
          <p className="text-sm font-nunito-medium text-[var(--bb-darkblue)]/80 mt-1">Sweet adventures await — let's build a box.</p>
          <div className="mt-5 flex flex-wrap gap-2 justify-center">
            <Button asChild className="h-12 px-5 rounded-xl bg-[var(--bb-cyan)] hover:bg-[var(--bb-cyan-deep)] text-white font-nunito-black">
              <Link to="/shop">Shop packs</Link>
            </Button>
            <Button asChild className="h-12 px-5 rounded-xl bg-[var(--bb-pink)] hover:bg-[var(--bb-pink-deep)] text-white font-nunito-black">
              <Link to="/customize">Customise a pack</Link>
            </Button>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="pb-32 md:pb-16" data-testid="cart-page">
      <section className="max-w-6xl mx-auto px-4 md:px-6 pt-4">
        <h1 className="font-display text-3xl md:text-4xl text-[var(--bb-darkblue)]">Your cart</h1>
        <p className="text-sm text-[var(--bb-darkgrey)] font-nunito-medium">{cart.length} item{cart.length > 1 ? 's' : ''}</p>
      </section>

      <section className="sticky top-14 z-30 bg-white border-y border-[var(--bb-darkblue)]/10">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-3">
          <FreeShippingBar />
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 md:px-6 mt-4 grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2 space-y-3">
          {cart.map((item) => (
            <div key={item.line_id} className="rounded-2xl bg-white border-2 border-[var(--bb-darkblue)]/10 p-3 flex gap-3" data-testid={`cart-item-${item.product_slug}`}>
              <div className="w-24 h-24 sm:w-28 sm:h-28 shrink-0 rounded-xl overflow-hidden bg-[var(--bb-yellow-soft)] relative">
                <img
                  src={imgSrc(item.customization?.preview_data_url || item.image, 'thumb')}
                  alt={item.name}
                  className="w-full h-full object-contain p-1"
                  loading="lazy"
                  decoding="async"
                />
                {item.pack && (
                  <div className="absolute bottom-1 left-1 right-1 text-center rounded-md bg-[var(--bb-darkblue)] text-white text-[10px] font-nunito-black px-1 py-0.5">
                    {item.pack.pack_size}×
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <Link to={`/product/${item.product_slug}`} className="text-sm font-nunito-black leading-snug hover:underline line-clamp-2 text-[var(--bb-darkblue)]">
                    {item.name}
                  </Link>
                  <button onClick={() => removeItem(item.line_id)} className="text-[var(--bb-darkgrey)] hover:text-[var(--bb-pink)] p-1 -mr-1" aria-label="Remove" data-testid={`cart-remove-${item.product_slug}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                {/* Pack composition */}
                {item.pack && (
                  <div className="mt-1.5" data-testid={`cart-pack-${item.product_slug}`}>
                    <Badge className="bg-[var(--bb-darkblue)] text-white rounded-full text-[10px] font-nunito-black inline-flex items-center gap-1">
                      <Package className="w-3 h-3" /> Pack of {item.pack.pack_size} {item.pack.pack_unit}
                    </Badge>
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {Object.entries(item.pack.selections || {}).filter(([, v]) => v > 0).slice(0, 6).map(([k, v]) => (
                        <span key={k} className="inline-flex items-center px-2 py-0.5 rounded-full bg-[var(--bb-yellow-soft)] text-[var(--bb-darkblue)] text-[10px] font-nunito-black">
                          {k.charAt(0).toUpperCase() + k.slice(1)} ×{v}
                        </span>
                      ))}
                      {Object.values(item.pack.selections || {}).filter((v) => v > 0).length > 6 && (
                        <span className="text-[10px] text-[var(--bb-darkgrey)] font-nunito-bold">+more</span>
                      )}
                    </div>
                  </div>
                )}

                {item.customization && (
                  <Badge className="mt-1.5 bg-[var(--bb-pink)] text-white rounded-full text-[10px] font-nunito-black inline-flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> Custom design saved
                  </Badge>
                )}
                {item.customization?.text && (
                  <div className="text-[11px] text-[var(--bb-darkgrey)] font-nunito-medium mt-0.5 line-clamp-1">Message: “{item.customization.text}”</div>
                )}

                <div className="mt-2 flex items-center justify-between">
                  <div className="inline-flex items-center rounded-xl border-2 border-[var(--bb-darkblue)]/10 overflow-hidden">
                    <button onClick={() => updateQty(item.line_id, item.qty - 1)} className="w-8 h-8 hover:bg-black/5" aria-label="Decrease"><Minus className="w-3.5 h-3.5 mx-auto" /></button>
                    <div className="w-8 text-center text-sm font-nunito-black">{item.qty}</div>
                    <button onClick={() => updateQty(item.line_id, item.qty + 1)} className="w-8 h-8 hover:bg-black/5" aria-label="Increase"><Plus className="w-3.5 h-3.5 mx-auto" /></button>
                  </div>
                  <div className="font-display text-lg text-[var(--bb-darkblue)]">{inr(item.price * item.qty)}</div>
                </div>
                {item.pack && (
                  <Link to={`/product/${item.product_slug}`} className="text-[11px] font-nunito-black text-[var(--bb-cyan)] hover:underline mt-1 inline-block" data-testid={`cart-edit-pack-${item.product_slug}`}>
                    Edit pack
                  </Link>
                )}
              </div>
            </div>
          ))}
          <button onClick={clearCart} className="text-xs text-[var(--bb-darkgrey)] hover:text-[var(--bb-pink)] font-nunito-bold" data-testid="cart-clear">Clear cart</button>
        </div>

        {/* Summary */}
        <aside className="rounded-2xl bg-white border-2 border-[var(--bb-darkblue)]/10 p-4 h-fit">
          <div className="font-nunito-black text-[var(--bb-darkblue)]">Order summary</div>
          <div className="mt-3 space-y-1.5 text-sm">
            <div className="flex justify-between font-nunito-medium"><span className="text-[var(--bb-darkgrey)]">Subtotal</span><span>{inr(subtotal)}</span></div>
            <div className="flex justify-between font-nunito-medium"><span className="text-[var(--bb-darkgrey)]">Shipping</span><span>{shipping === 0 ? <span className="text-[var(--bb-green)] font-nunito-black">FREE</span> : inr(shipping)}</span></div>
            <div className="border-t-2 border-[var(--bb-darkblue)]/10 pt-2 mt-2 flex justify-between text-base font-display"><span className="text-[var(--bb-darkblue)]">Total</span><span data-testid="cart-total" className="text-[var(--bb-darkblue)]">{inr(total)}</span></div>
          </div>
          <Button onClick={handleCheckout} disabled={redirecting} className="w-full h-12 rounded-xl mt-4 bg-[var(--bb-cyan)] hover:bg-[var(--bb-cyan-deep)] text-white font-nunito-black hidden md:flex" data-testid="cart-checkout-button">
            {redirecting ? 'Redirecting…' : (<>{shopifyEnabled ? 'Checkout on Shopify' : 'Checkout'} <ChevronRight className="w-4 h-4 ml-1" /></>)}
          </Button>
        </aside>
      </section>

      <Footer />

      {/* Sticky checkout bar (mobile) */}
      <div
        className="fixed left-0 right-0 z-40 bg-white border-t border-[var(--bb-darkblue)]/10 md:hidden"
        style={{ bottom: 'calc(64px + env(safe-area-inset-bottom))' }}
        data-testid="cart-sticky-checkout-bar"
      >
        <div className="max-w-6xl mx-auto px-3 py-2.5 flex items-center gap-2">
          <div className="flex-1">
            <div className="text-xs text-[var(--bb-darkgrey)] font-nunito-bold">Total</div>
            <div className="font-display text-lg text-[var(--bb-darkblue)]">{inr(total)}</div>
          </div>
          <Button onClick={handleCheckout} disabled={redirecting} className="h-11 px-5 rounded-xl bg-[var(--bb-cyan)] hover:bg-[var(--bb-cyan-deep)] text-white font-nunito-black" data-testid="cart-checkout-button-mobile">
            {redirecting ? 'Redirecting…' : (<>Checkout <ChevronRight className="w-4 h-4 ml-1" /></>)}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
